/**
 * Utility functions for exporting SEO actions as markdown
 */

/**
 * Sorts actions based on user requirements:
 * 1. Status: not done at top, done at bottom
 * 2. Impact: high to low
 * 3. Category: alphabetical
 */
export function sortActionsForExport(actions) {
  return [...actions].sort((a, b) => {
    // 1. Status priority (not done first, done last)
    const statusA = a.status === 'completed' ? 2 : a.status === 'in-progress' ? 1 : 0;
    const statusB = b.status === 'completed' ? 2 : b.status === 'in-progress' ? 1 : 0;
    
    if (statusA !== statusB) {
      return statusA - statusB;
    }
    
    // 2. Impact priority (high to low)
    const impactOrder = { 'high': 3, 'medium': 2, 'low': 1 };
    const impactA = impactOrder[a.impact?.toLowerCase()] || 0;
    const impactB = impactOrder[b.impact?.toLowerCase()] || 0;
    
    if (impactA !== impactB) {
      return impactB - impactA; // Descending order
    }
    
    // 3. Category alphabetical
    const categoryA = (a.category || '').toLowerCase();
    const categoryB = (b.category || '').toLowerCase();
    
    return categoryA.localeCompare(categoryB);
  });
}

/**
 * Groups actions by status for better organization
 */
export function groupActionsByStatus(actions) {
  const sortedActions = sortActionsForExport(actions);
  
  const groups = {
    'not-started': [],
    'in-progress': [],
    'completed': []
  };
  
  sortedActions.forEach(action => {
    const status = action.status === 'completed' ? 'completed' : 
                   action.status === 'in-progress' ? 'in-progress' : 'not-started';
    groups[status].push(action);
  });
  
  return groups;
}

/**
 * Formats action status for display
 */
export function getStatusDisplayName(status) {
  switch (status) {
    case 'not-started': return 'Not Started';
    case 'in-progress': return 'In Progress';
    case 'completed': return 'Completed';
    default: return status;
  }
}

/**
 * Formats action impact for display
 */
export function getImpactIcon(impact) {
  switch (impact?.toLowerCase()) {
    case 'high': return '🔴';
    case 'medium': return '🟡';
    case 'low': return '🟢';
    default: return '⚪';
  }
}

/**
 * Formats action difficulty for display
 */
export function getDifficultyIcon(difficulty) {
  switch (difficulty?.toLowerCase()) {
    case 'hard': return '🔴';
    case 'medium': return '🟡';
    case 'easy': return '🟢';
    default: return '⚪';
  }
}

/**
 * Generates markdown content for all SEO actions
 */
export function generateMarkdownExport(actions, domain, executiveSummary = null) {
  const timestamp = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  const groupedActions = groupActionsByStatus(actions);
  const totalActions = actions.length;
  const completedActions = groupedActions.completed.length;
  const inProgressActions = groupedActions['in-progress'].length;
  const notStartedActions = groupedActions['not-started'].length;
  
  let markdown = `# SEO Action Plan Export\n\n`;
  markdown += `**Domain:** ${domain}\n`;
  markdown += `**Export Date:** ${timestamp}\n`;
  markdown += `**Total Actions:** ${totalActions}\n\n`;
  
  // Summary statistics
  markdown += `## Summary\n\n`;
  markdown += `- ✅ **Completed:** ${completedActions} actions\n`;
  markdown += `- 🔄 **In Progress:** ${inProgressActions} actions\n`;
  markdown += `- ⏳ **Not Started:** ${notStartedActions} actions\n`;
  markdown += `- 📊 **Progress:** ${Math.round((completedActions / totalActions) * 100)}%\n\n`;
  
  // Executive Summary if available
  if (executiveSummary) {
    markdown += `## Executive Summary\n\n`;
    markdown += `${executiveSummary}\n\n`;
  }
  
  // Actions by status
  const statusOrder = ['not-started', 'in-progress', 'completed'];
  
  statusOrder.forEach(status => {
    const statusActions = groupedActions[status];
    if (statusActions.length === 0) return;
    
    const statusDisplayName = getStatusDisplayName(status);
    const statusIcon = status === 'completed' ? '✅' : status === 'in-progress' ? '🔄' : '⏳';
    
    markdown += `## ${statusIcon} ${statusDisplayName} (${statusActions.length})\n\n`;
    
    statusActions.forEach((action, index) => {
      const actionNumber = index + 1;
      const impactIcon = getImpactIcon(action.impact);
      const difficultyIcon = getDifficultyIcon(action.difficulty);
      const categoryTag = action.category ? `**Category:** ${action.category}` : '';
      const impactTag = action.impact ? `**Impact:** ${impactIcon} ${action.impact}` : '';
      const difficultyTag = action.difficulty ? `**Difficulty:** ${difficultyIcon} ${action.difficulty}` : '';
      const timeTag = action.time_available ? `**Time Required:** ${action.time_available}` : '';
      
      markdown += `### ${actionNumber}. ${action.title || 'Unnamed Action'}\n\n`;
      
      // Metadata tags
      const tags = [categoryTag, impactTag, difficultyTag, timeTag].filter(Boolean);
      if (tags.length > 0) {
        markdown += `${tags.join(' | ')}\n\n`;
      }
      
      // Problem
      if (action.problem) {
        markdown += `**Problem:**\n${action.problem}\n\n`;
      }
      
      // Solution
      if (action.solution) {
        markdown += `**Solution:**\n${action.solution}\n\n`;
      }
      
      // Implementation
      if (action.implementation) {
        markdown += `**Implementation:**\n${action.implementation}\n\n`;
      }
      
      markdown += `---\n\n`;
    });
  });
  
  // Footer
  markdown += `## Export Information\n\n`;
  markdown += `This export was generated from GA4Hell.com's SEO Action Dashboard.\n`;
  markdown += `Generated on ${timestamp} for ${domain}\n\n`;
  markdown += `**Legend:**\n`;
  markdown += `- Impact: 🔴 High | 🟡 Medium | 🟢 Low\n`;
  markdown += `- Difficulty: 🔴 Hard | 🟡 Medium | 🟢 Easy\n`;
  markdown += `- Status: ⏳ Not Started | 🔄 In Progress | ✅ Completed\n`;
  
  return markdown;
}

/**
 * Downloads the markdown content as a file
 */
export function downloadMarkdownFile(content, domain) {
  const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const filename = `seo-actions-${domain}-${timestamp}.md`;
  
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Main export function that generates and downloads the markdown file
 */
export function exportActionsAsMarkdown(actions, domain, executiveSummary = null) {
  const markdown = generateMarkdownExport(actions, domain, executiveSummary);
  downloadMarkdownFile(markdown, domain);
  
  return {
    success: true,
    filename: `seo-actions-${domain}-${new Date().toISOString().slice(0, 10)}.md`,
    actionCount: actions.length
  };
}